import api from './api';

export type OrderStatus = 'pending' | 'paid' | 'completed' | 'cancelled' | 'refunded';
export type PaymentStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface OrderItem {
    id: number;
    title: string;
    priceType: string;
    unitPrice: string;
    quantity: number;
    discount: string;
    total: string;
    productId: number | null;
}

export interface Order {
    id: number;
    uuid: string;
    orderNumber: string;
    status: OrderStatus;
    paymentStatus: PaymentStatus;
    customerName: string;
    customerEmail: string;
    subtotal: string;
    discount: string;
    tax: string;
    total: string;
    currency: string;
    items: OrderItem[];
    createdAt: string;
    paidAt: string | null;
    completedAt: string | null;
}

export interface OrderListResponse {
    orders: Order[];
    count: number;
}

export interface OrderStatistics {
    total_orders: number;
    completed_orders: number;
    pending_orders: number;
    total_revenue: number;
}

export interface CheckoutData {
    email: string;
    name: string;
    billingAddress?: {
        street?: string;
        city?: string;
        postalCode?: string;
        country?: string;
    };
}

export interface CheckoutResponse {
    success: boolean;
    order: Order;
}

export interface PurchaseCheckResponse {
    productId: number;
    purchased: boolean;
}

class OrderService {
    /**
     * Get all orders for current user
     */
    async getOrders(): Promise<Order[]> {
        const response = await api.get<OrderListResponse>('/orders');
        return response.data.orders;
    }

    /**
     * Get order by ID
     */
    async getOrder(id: number): Promise<Order> {
        const response = await api.get<{ order: Order }>(`/orders/${id}`);
        return response.data.order;
    }

    /**
     * Get order by order number
     */
    async getOrderByNumber(orderNumber: string): Promise<Order> {
        const response = await api.get<{ order: Order }>(`/orders/number/${orderNumber}`);
        return response.data.order;
    }

    /**
     * Create order from cart (checkout)
     */
    async checkout(data: CheckoutData): Promise<Order> {
        const response = await api.post<CheckoutResponse>('/orders/checkout', data);
        return response.data.order;
    }

    /**
     * Cancel an order
     */
    async cancelOrder(id: number, reason?: string): Promise<Order> {
        const response = await api.post<{ success: boolean; order: Order }>(
            `/orders/${id}/cancel`,
            { reason }
        );
        return response.data.order;
    }

    /**
     * Check if user has purchased a product
     */
    async checkPurchase(productId: number): Promise<boolean> {
        const response = await api.get<PurchaseCheckResponse>(`/orders/check-purchase/${productId}`);
        return response.data.purchased;
    }

    /**
     * Get order statistics (admin)
     */
    async getStatistics(): Promise<OrderStatistics> {
        const response = await api.get<{ statistics: OrderStatistics }>('/orders/statistics');
        return response.data.statistics;
    }

    /**
     * Get company orders (admin)
     */
    async getCompanyOrders(status?: OrderStatus): Promise<Order[]> {
        const params = status ? `?status=${status}` : '';
        const response = await api.get<OrderListResponse>(`/orders/company${params}`);
        return response.data.orders;
    }

    /**
     * Update order status (admin)
     */
    async updateStatus(id: number, status: OrderStatus): Promise<Order> {
        const response = await api.patch<{ success: boolean; order: Order }>(
            `/orders/${id}/status`,
            { status }
        );
        return response.data.order;
    }

    /**
     * Format order total for display
     */
    formatTotal(order: Order): string {
        const amount = parseFloat(order.total);
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: order.currency || 'EUR',
        }).format(amount);
    }

    /**
     * Get status display label
     */
    getStatusLabel(status: OrderStatus): string {
        const labels: Record<OrderStatus, string> = {
            pending: 'Ausstehend',
            paid: 'Bezahlt',
            completed: 'Abgeschlossen',
            cancelled: 'Storniert',
            refunded: 'Erstattet',
        };
        return labels[status] || status;
    }

    /**
     * Get payment status display label
     */
    getPaymentStatusLabel(status: PaymentStatus): string {
        const labels: Record<PaymentStatus, string> = {
            pending: 'Ausstehend',
            processing: 'In Bearbeitung',
            completed: 'Abgeschlossen',
            failed: 'Fehlgeschlagen',
        };
        return labels[status] || status;
    }

    /**
     * Get status color for MUI
     */
    getStatusColor(status: OrderStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
        const colors: Record<OrderStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
            pending: 'warning',
            paid: 'info',
            completed: 'success',
            cancelled: 'error',
            refunded: 'secondary',
        };
        return colors[status] || 'default';
    }

    /**
     * Get payment status color for MUI
     */
    getPaymentStatusColor(status: PaymentStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
        const colors: Record<PaymentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
            pending: 'warning',
            processing: 'info',
            completed: 'success',
            failed: 'error',
        };
        return colors[status] || 'default';
    }

    /**
     * Check if order can be cancelled
     */
    canCancel(order: Order): boolean {
        return order.status === 'pending' || order.status === 'paid';
    }

    /**
     * Check if order is fully completed
     */
    isCompleted(order: Order): boolean {
        return order.status === 'completed' && order.paymentStatus === 'completed';
    }

    /**
     * Format date for display
     */
    formatDate(dateString: string | null): string {
        if (!dateString) return '-';
        return new Intl.DateTimeFormat('de-DE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(dateString));
    }

    /**
     * Get total items count in order
     */
    getItemCount(order: Order): number {
        return order.items.reduce((sum, item) => sum + item.quantity, 0);
    }
}

export const orderService = new OrderService();
export default orderService;
